import {createAction} from '@reduxjs/toolkit'

import {
  ClaimsType,
  SocialAuthenticationMethod,
  SourceComponentType,
  TrackUserSignInClickSourceComponent,
  User
} from '../types/Auth'
import {UserPhoneNumberErrors} from '../types/Passwordless'

/**
 * A timeout that gives the analytics systems to send the request before the application
 * sends the user away.
 */
export const TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY = 50

/**
 * Fires an action to track that a validation error over the phone number happened
 * @param payload - {@link UserPhoneNumberErrors}
 */
export const trackPhoneNumberValidationError = createAction<
  UserPhoneNumberErrors
>('authentication/trackPhoneNumberValidationError')

/**
 * Fires an action that tells the application to set the audience token in the browser
 * @param payload - The Audience Token
 */
export const setAudienceTokenInBrowser = createAction<string>(
  'authentication/setAudienceTokenInBrowser'
)

/**
 * Fires an action to tracks that the user initiated a sign in process
 * @param sourceComponent - The component that initiated the action
 * @deprecated - use trackUserAuthClick instead
 */
export const trackUserSignInClick = createAction<
  TrackUserSignInClickSourceComponent
>('authentication/trackUserSignInClick')

/**
 * Fires an action to tracks that the user clicked on authentication buttons inside the overlay
 */
export const trackUserAuthClick = createAction(
  'authentication/trackUserAuthClick',
  ({
    sourceComponent,
    authenticationMethod
  }: {
    sourceComponent: SourceComponentType
    authenticationMethod: SocialAuthenticationMethod
  }) => ({
    payload: {
      sourceComponent,
      authenticationMethod
    }
  })
)

/**
 * Fires an action to tracks places where user open the auth overlay with a SignUp button
 */
export const trackSignUpClick = createAction<SourceComponentType>(
  'authentication/trackSignUpClick'
)

/**
 * Fires an action to tracks places where user open the auth overlay with a Login button
 */
export const trackLoginClick = createAction<SourceComponentType>(
  'authentication/trackLoginClick'
)

/**
 * Fires an action that tells the application the auth process was successful
 * @param claims - The user {@link ClaimsType | claims}
 * @param sourceComponent - The component that initiated the action
 */
export const userAuthenticationSuccess = createAction(
  'authentication/userAuthenticationSuccess',
  (claims: ClaimsType, sourceComponent: string) => ({
    payload: {
      claims,
      sourceComponent
    }
  })
)

/**
 * Fires an action that tells the application that the user information is ready
 * for identification on our systems
 * @param user - The user {@link User}
 */
export const identifyAuthenticatedUser = createAction<User>(
  'authentication/identifyAuthenticatedUser'
)
